import type { SettingsFlow, UpdateSettingsFlowBody } from "@ory/kratos-client";
import type React from "react";
import type { Dispatch } from "react";
import { Link, useSearchParams } from "react-router";
import { AccordionMenu } from "~/components/Accordion";
import { Alert } from "~/components/Alert";
import {
	ContentContainer,
	ContentContainerWrapper,
} from "~/components/ContentContainer";
import { matchFlowQuery, type NodeQuery } from "~/components/KratosFlow";
import { DefaultLayout } from "~/components/Layout";
import { LookupSecretSettings } from "~/pages/Settings/LookupSecretSettings";
import { TotpSettings } from "~/pages/Settings/TotpSettings";
import { WebAuthnSettings } from "~/pages/Settings/WebAuthnSettings";

interface Props {
	flow: SettingsFlow;
	onSubmit: (values: UpdateSettingsFlowBody) => Promise<void>;
	only?: string;
	setMethod: Dispatch<string>;
}

export const MfaSettings: React.FC<Props> = ({
	flow,
	onSubmit,
	only,
	setMethod,
}) => {
	const [searchParams] = useSearchParams();
	const alertWarning = searchParams.get("alert_warning");

	const configuredPrimaryMethodsQuery: NodeQuery[] = [
		{ name: "webauthn_remove" },
		{ name: "totp_unlink" },
	];
	const countConfiguredPrimaryMethods = flow.ui.nodes.filter((node) =>
		configuredPrimaryMethodsQuery.some((query) => matchFlowQuery(node, query)),
	).length;
	const configuredMethodsQuery: NodeQuery[] = [
		{ name: "webauthn_remove" },
		{ name: "totp_unlink" },
		{ name: "lookup_secret_disable" },
	];
	const countConfiguredMethods = flow.ui.nodes.filter((node) =>
		configuredMethodsQuery.some((query) => matchFlowQuery(node, query)),
	).length;
	return (
		<>
			<title>Multi-Factor Authentication Settings | TIER IV Account</title>
			<DefaultLayout>
				<ContentContainerWrapper>
					<ContentContainer size="lg">
						{countConfiguredMethods === 1 && (
							<Alert type="warning" withIcon>
								<span className="font-semibold">
									Please set up 2 or more to make sure that you can still
									recover your account.
								</span>
								If you lose all credentials, you cannot recover your account.
							</Alert>
						)}
						{alertWarning && (
							<Alert type="warning" withIcon>
								{alertWarning}
							</Alert>
						)}
						<section className="mt-12">
							<AccordionMenu
								title="SECURITY KEYS"
								show={only === "webauthn"}
								onShow={() => setMethod("webauthn")}
							>
								<WebAuthnSettings flow={flow} onSubmit={onSubmit} />
							</AccordionMenu>
							<AccordionMenu
								title="AUTHENTICATOR APP"
								show={only === "totp"}
								onShow={() => setMethod("totp")}
							>
								<TotpSettings flow={flow} onSubmit={onSubmit} />
							</AccordionMenu>
							<AccordionMenu
								title="RECOVERY CODES"
								show={only === "lookup_secret"}
								onShow={() => setMethod("lookup_secret")}
								disable={countConfiguredPrimaryMethods === 0}
							>
								<LookupSecretSettings flow={flow} onSubmit={onSubmit} />
							</AccordionMenu>
						</section>
						<p className="mt-20 text-center">
							<Link to="/settings">Go back to account settings</Link>
						</p>
					</ContentContainer>
				</ContentContainerWrapper>
			</DefaultLayout>
		</>
	);
};
